"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isListBoxSection = exports.isPersistentKey = exports.isPersistentItem = exports.persistentItemPrefix = exports.ChipSelectField = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const change_case_1 = require("change-case");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_stately_1 = require("react-stately");
const components_1 = require("../components");
const internal_1 = require("../components/internal");
const Label_1 = require("../components/Label");
const PresentationContext_1 = require("../components/PresentationContext");
const Css_1 = require("../Css");
const ChipTextField_1 = require("./ChipTextField");
const ListBox_1 = require("./internal/ListBox");
const ListBoxChip_1 = require("./internal/ListBoxChip");
const Value_1 = require("./Value");
const utils_1 = require("../utils");
const options_1 = require("../utils/options");
function ChipSelectField(props) {
    var _a, _b;
    const firstRender = (0, react_1.useRef)(true);
    const { fieldProps } = (0, PresentationContext_1.usePresentationContext)();
    const { label, value, disabled = false, placeholder = "Select an option", options, onSelect, getOptionValue = options_1.defaultOptionValue, // if unset, assume O implements HasId
    getOptionLabel = options_1.defaultOptionLabel, // if unset, assume O implements HasName
    onFocus, onBlur, clearable = false, onCreateNew, } = props;
    const tid = (0, utils_1.useTestIds)(props, "chipSelectField");
    const typeScale = (_a = fieldProps === null || fieldProps === void 0 ? void 0 : fieldProps.typeScale) !== null && _a !== void 0 ? _a : "sm";
    const isDisabled = !!disabled;
    const showClearButton = !disabled && clearable && !!value;
    const chipStyles = (0, react_1.useMemo)(() => Css_1.Css[typeScale].tl.bgGray300.gray900.br16.pxPx(10).pyPx(2).$, [typeScale]);
    // Controls showing the focus border styles.
    const [visualFocus, setVisualFocus] = (0, react_1.useState)(false);
    const [isClearFocused, setIsClearFocused] = (0, react_1.useState)(false);
    const { focusProps } = (0, react_aria_1.useFocus)({
        onFocus: (e) => {
            // Do not call `onFocus` if focus was returned to the trigger element from the Popover
            if (popoverRef.current && popoverRef.current.contains(e.relatedTarget)) {
                return;
            }
            (0, utils_1.maybeCall)(onFocus);
        },
        onBlur: (e) => {
            // Do not call onBlur if focus moved to within the Popover
            if ((popoverRef.current && popoverRef.current.contains(e.relatedTarget)) || showInput) {
                return;
            }
            (0, utils_1.maybeCall)(onBlur);
        },
        // Do not change visual focus state if select menu is opened
        onFocusChange: (isFocused) => !state.isOpen && setVisualFocus(isFocused),
    });
    const { focusProps: clearFocusProps } = (0, react_aria_1.useFocus)({ onFocusChange: setIsClearFocused });
    const buttonRef = (0, react_1.useRef)(null);
    const popoverRef = (0, react_1.useRef)(null);
    const listBoxRef = (0, react_1.useRef)(null);
    const wrapperRef = (0, react_1.useRef)(null);
    // Using `ListData` in order to dynamically update the items
    const listData = (0, react_stately_1.useListData)({
        initialItems: !onCreateNew
            ? options
            : [
                { title: "Options", options },
                { title: "Actions", isPersistent: true, options: [createNewOpt] },
            ],
        initialSelectedKeys: [(0, Value_1.valueToKey)(value)],
        getKey: (item) => (isListBoxSection(item) ? item.title : getOptionValue(item)),
    });
    (0, react_1.useEffect)(() => {
        // Avoid unnecessary update of `options` on first render. We define the initial set of items based on the options in the `useListData` hook.
        if (!firstRender.current) {
            if (onCreateNew) {
                // if we have the options in a section, update that section
                listData.update("Options", { title: "Options", options });
            }
            else {
                // otherwise, reset the list completely. We could traverse through the list and update/add/remove when needed, though this is simpler for now.
                listData.remove(...state.collection.getKeys());
                listData.append(...options);
            }
        }
        firstRender.current = false;
    }, [options]);
    const selectChildren = (0, react_1.useMemo)(() => listData.items.map((s) => {
        if (isListBoxSection(s)) {
            return ((0, jsx_runtime_1.jsx)(react_stately_1.Section, { title: s.title, items: s.options, children: (item) => {
                    if (isPersistentItem(item)) {
                        return ((0, jsx_runtime_1.jsx)(react_stately_1.Item, { textValue: item.name, children: item.name }, item.id));
                    }
                    const label = getOptionLabel(item);
                    return ((0, jsx_runtime_1.jsx)(react_stately_1.Item, { textValue: label, children: (0, jsx_runtime_1.jsx)(ListBoxChip_1.ListBoxChip, { label: label }) }, getOptionValue(item)));
                } }, (0, change_case_1.camelCase)(s.title)));
        }
        const label = getOptionLabel(s);
        return ((0, jsx_runtime_1.jsx)(react_stately_1.Item, { textValue: label, children: (0, jsx_runtime_1.jsx)(ListBoxChip_1.ListBoxChip, { label: label }) }, getOptionValue(s)));
    }), [listData.items, getOptionLabel, getOptionValue]);
    const selectHookProps = {
        label,
        isDisabled,
        items: listData.items,
        children: selectChildren,
        autoFocus: true,
        disallowEmptySelection: false,
    };
    const state = (0, react_stately_1.useSelectState)({
        ...selectHookProps,
        selectedKey: (0, Value_1.valueToKey)(value),
        onSelectionChange: (key) => {
            if (key === createNewOpt.id) {
                setShowInput(true);
                return;
            }
            const selectedItem = options.find((o) => getOptionValue(o) === key);
            if (selectedItem) {
                onSelect(key, selectedItem);
            }
            // Per UX, we do not want to show visual focus upon selection change.
            setVisualFocus(false);
        },
        onOpenChange: (isOpen) => {
            var _a;
            if (!isOpen) {
                // When closing, reset the focus to the button element. This is to retain "tab position" in the document, allowing hte user to hit "Tab" and move to the next tabbable element.
                // If the menu closed due to a user selecting an option, then the field will not visually appear focused.
                (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
            else {
                // If opened, set visual focus to true. It is possible to be in a state where the browser focus is on this element, but we are not "visually" focused (see `onSelectionChange`). If the user opens the menu again, we should trigger the visual focus.
                setVisualFocus(true);
            }
        },
    });
    const { labelProps, triggerProps, valueProps, menuProps } = (0, react_aria_1.useSelect)(selectHookProps, state, buttonRef);
    const { buttonProps } = (0, react_aria_1.useButton)({ ...triggerProps, isDisabled }, buttonRef);
    const { overlayProps } = (0, react_aria_1.useOverlayPosition)({
        targetRef: buttonRef,
        overlayRef: popoverRef,
        scrollRef: listBoxRef,
        shouldFlip: true,
        isOpen: state.isOpen,
        onClose: state.close,
        placement: "bottom left",
        offset: 8,
    });
    overlayProps.style = {
        ...overlayProps.style,
        width: (_b = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth,
        // Ensures the menu never gets too small.
        minWidth: 200,
    };
    // State management for the "Create new" flow with ChipTextField.
    const [showInput, setShowInput] = (0, react_1.useState)(false);
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [showInput && onCreateNew && ((0, jsx_runtime_1.jsx)(CreateNewField, { onBlur: () => setShowInput(false), onEnter: async (value) => {
                    await onCreateNew(value);
                    setShowInput(false);
                }, ...tid.createNewField })), (0, components_1.maybeTooltip)({
                title: (0, components_1.resolveTooltip)(disabled),
                placement: "top",
                children: ((0, jsx_runtime_1.jsxs)("div", { ref: wrapperRef, css: {
                        ...chipStyles,
                        ...Css_1.Css.dif.relative.p0.mwPx(32).if(!value).bgGray200.$,
                        ...(visualFocus ? Css_1.Css.bshFocus.$ : {}),
                        ...(showInput ? Css_1.Css.dn.$ : {}),
                    }, children: [(0, jsx_runtime_1.jsx)(Label_1.Label, { label: label, labelProps: labelProps, hidden: true, ...tid.label }), (0, jsx_runtime_1.jsx)("button", { ...(0, react_aria_1.mergeProps)(focusProps, buttonProps), ref: buttonRef, css: {
                                ...Css_1.Css.tl.br16.pxPx(10).pyPx(2).outline0.if(showClearButton).prPx(4).borderRadius("16px 0 0 16px").$,
                                ...(isDisabled ? Css_1.Css.cursorNotAllowed.gray700.$ : {}),
                                "&:hover:not(:disabled)": Css_1.Css.bgGray400.if(!value).bgGray300.$,
                            }, title: state.selectedItem ? state.selectedItem.textValue : placeholder, ...tid, children: (0, jsx_runtime_1.jsx)("span", { ...valueProps, css: Css_1.Css.lineClamp1.breakAll.$, children: state.selectedItem ? state.selectedItem.textValue : placeholder }) }), showClearButton && ((0, jsx_runtime_1.jsx)("button", { ...clearFocusProps, css: {
                                ...Css_1.Css.prPx(4).borderRadius("0 16px 16px 0").outline0.$,
                                "&:hover": Css_1.Css.bgGray400.$,
                                ...(isClearFocused ? Css_1.Css.boxShadow(`0px 0px 0px 2px rgba(3,105,161,1)`).$ : {}),
                            }, onClick: () => {
                                onSelect(undefined, undefined);
                                setIsClearFocused(false);
                            }, "aria-label": "Remove", ...tid.clearButton, children: (0, jsx_runtime_1.jsx)(components_1.Icon, { icon: "x", inc: typeScale === "xs" ? 2 : undefined }) }))] })),
            }), state.isOpen && ((0, jsx_runtime_1.jsx)(internal_1.Popover, { triggerRef: buttonRef, popoverRef: popoverRef, positionProps: overlayProps, onClose: state.close, isOpen: state.isOpen, shouldCloseOnBlur: true, children: (0, jsx_runtime_1.jsx)(ListBox_1.ListBox, { ...menuProps, listBoxRef: listBoxRef, state: state, getOptionLabel: getOptionLabel, getOptionValue: getOptionValue, positionProps: overlayProps }) }))] }));
}
exports.ChipSelectField = ChipSelectField;
exports.persistentItemPrefix = "persistentItem:";
const createNewOpt = {
    id: `${exports.persistentItemPrefix}createNew`,
    name: "Create new",
};
function isPersistentItem(opt) {
    return typeof opt === "object" && "id" in opt && isPersistentKey(opt.id);
}
exports.isPersistentItem = isPersistentItem;
function isPersistentKey(key) {
    return typeof key === "string" && key.startsWith(exports.persistentItemPrefix);
}
exports.isPersistentKey = isPersistentKey;
function isListBoxSection(obj) {
    return obj && typeof obj === "object" && "options" in obj;
}
exports.isListBoxSection = isListBoxSection;
// Wrapper for the ChipTextField used in the "Create New" flow on ChipSelectField
function CreateNewField(props) {
    const { onBlur, onEnter } = props;
    const [value, setValue] = (0, react_1.useState)("Add new");
    const tid = (0, utils_1.useTestIds)(props);
    return ((0, jsx_runtime_1.jsx)(ChipTextField_1.ChipTextField, { autoFocus: true, label: "Add new", value: value, onChange: setValue, onEnter: () => onEnter(value), onBlur: onBlur, ...tid }));
}
